import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const EASE = [0.22, 0, 0.36, 1];
const DURATION = 0.9;

// Runs the full curtain sequence around every route change:
// 1. Curtain closes over the current page (900ms, anchored at the bottom).
// 2. The instant it's fully closed, the new page swaps in underneath it.
// 3. Curtain opens to reveal the new page (900ms, anchored at the top).
// A single persistent <motion.div> drives both halves — its animate
// target and transform-origin just flip between phases — rather than
// two separate elements, so there's one continuous, easy-to-reason-about
// state machine instead of coordinating two independent animations.
const PageTransitionWrapper = ({ element, location }) => {
  const [displayElement, setDisplayElement] = useState(element);
  const [displayPath, setDisplayPath] = useState(location.pathname);
  const [phase, setPhase] = useState("idle"); // idle | closing | opening
  const pendingRef = useRef(null);

  useEffect(() => {
    if (location.pathname !== displayPath && phase === "idle") {
      pendingRef.current = { element, path: location.pathname };
      setPhase("closing");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {displayElement}
      <motion.div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backgroundColor: "#111",
          zIndex: 300,
          transformOrigin: phase === "opening" ? "top" : "bottom",
          pointerEvents: phase === "idle" ? "none" : "auto",
        }}
        initial={{ scaleY: 0 }}
        animate={{ scaleY: phase === "closing" ? 1 : 0 }}
        transition={{ duration: DURATION, ease: EASE }}
        onAnimationComplete={() => {
          if (phase === "closing" && pendingRef.current) {
            setDisplayElement(pendingRef.current.element);
            setDisplayPath(pendingRef.current.path);
            pendingRef.current = null;
            setPhase("opening");
          } else if (phase === "opening") {
            setPhase("idle");
          }
        }}
      />
    </>
  );
};

export default PageTransitionWrapper;
